// A planned batch (`fill.ts`) run: the big red message for a value refused, the "Are you sure?" for
// the date rules, then the writer's `saveMany` — or, for the Appointment date, the page's scheduling
// (`onAppointment`: one Schedule pickup for every line). One place for Set value…, the fill down and a paste.

import { formatDay, poPtText } from "@app/shared";
import type { LineView, Row } from "@app/shared";
import type { ColumnDef } from "./columns";
import type { FillPlan } from "./fill";
import { batchNote, fillPreview } from "./fill";
import { notify, useMessage } from "./message";
import type { Writer } from "./writer";

type Message = ReturnType<typeof useMessage>;

const lineWords = (row: Row) => `${row.line.customerName} · ${poPtText(row.line)}`;

export async function runFill({
  def,
  plan,
  kind,
  writer,
  message,
  onAppointment,
  rowNumber,
  unused = 0,
  done,
}: {
  def: ColumnDef;
  plan: FillPlan;
  kind: "set" | "fill" | "paste";
  writer: Writer;
  message: Message;
  /** The Appointment date: the page schedules the lines (item 8). */
  onAppointment?: (lines: LineView[], date: string, done: () => void) => void;
  /** A row's number on the grid, for a paste stopped at it. */
  rowNumber?: (row: Row) => number;
  /** A paste's values left over below the last row. */
  unused?: number;
  /** Called once nothing more is asked (the grid takes the focus back). */
  done?: () => void;
}): Promise<void> {
  const finish = () => done?.();
  const stopped = plan.refused;
  // Set value and a fill down write nothing when a value is refused; a paste writes what came before it.
  if (stopped && kind !== "paste") {
    await message.error(stopped.title, stopped.message, [`${lineWords(stopped.row)}: “${stopped.raw}”`]);
    return finish();
  }
  if (plan.warnings.length) {
    const ok = await message.confirm(
      "Are you sure?",
      `Save ${def.header} on ${plan.writes.length} line${plan.writes.length === 1 ? "" : "s"}? Check these first:`,
      {
        lines: plan.warnings.flatMap((w) => w.messages.map((m) => `${lineWords(w.row)} — ${m}`)),
        confirmLabel: "Save anyway",
      },
    );
    if (!ok) return finish();
  }
  if (!plan.writes.length && !stopped) {
    notify("Nothing to change", fillPreview(def, plan));
    return finish();
  }
  if (def.field === "appointmentDate" && plan.writes.length) {
    const dates = new Set(plan.writes.map((w) => w.value));
    if (dates.size > 1 || !onAppointment) {
      await message.error(
        "One Appointment date at a time",
        "An Appointment date schedules a pickup: paste or fill one date, or use Schedule pickup.",
        [...dates].map((d) => (d ? formatDay(d) : "(empty)")),
      );
      return finish();
    }
    onAppointment(
      plan.writes.map((w) => w.row.line),
      [...dates][0]!,
      finish,
    );
    return;
  }
  const title =
    kind === "set" ? `Setting ${def.header}` : kind === "fill" ? `Filling ${def.header}` : `Pasting into ${def.header}`;
  await writer.saveMany(
    def.field!,
    plan.writes.map((w) => ({ line: w.row.line, value: w.value })),
    batchNote(kind, plan.writes.length),
    title,
  );
  if (stopped)
    await message.error(
      `Paste stopped at row ${rowNumber?.(stopped.row) ?? ""}`.trim(),
      `${stopped.message} The rows above it are saved; nothing from this row down.`,
      [`${lineWords(stopped.row)}: “${stopped.raw}”`],
    );
  else if (unused)
    notify(`${unused} pasted value${unused === 1 ? "" : "s"} had no row`, "The paste went past the last row shown.");
  finish();
}
